import type { CountryWithRegionDto } from "app/shared/api/getCountriesWithRegions";
import type { RegionDto } from "app/shared/api/getRegions";
import type { RegionManagerState } from "./reducer";

const selectSlice = (state: any): RegionManagerState => state?.RegionManager ?? state?.regionManager ?? ({} as RegionManagerState);

export const selectCountries = (state: any): CountryWithRegionDto[] => selectSlice(state).countries ?? [];
export const selectRegions = (state: any): RegionDto[] => selectSlice(state).regions ?? [];
export const selectCountriesLoading = (state: any): boolean => !!selectSlice(state).countriesLoading;
export const selectCountriesError = (state: any): string | undefined => selectSlice(state).countriesError;
export const selectRegionsLoading = (state: any): boolean => !!selectSlice(state).regionsLoading;
export const selectRegionsError = (state: any): string | undefined => selectSlice(state).regionsError;

export const selectInheritFromDivision = (state: any): boolean => !!selectSlice(state).inheritFromDivision;
export const selectSourceDivisionId = (state: any): number | null => selectSlice(state).sourceDivisionId ?? null;
export const selectPersistedInheritedFromDivisionId = (state: any): number | null =>
  selectSlice(state).persistedInheritedFromDivisionId ?? null;

export const selectCountryEdits = (state: any): Record<string, number | null> => selectSlice(state).countryEdits ?? {};
export const selectSelectedCountryIds = (state: any): string[] => selectSlice(state).selectedCountryIds ?? [];
export const selectSearchTerm = (state: any): string => selectSlice(state).searchTerm ?? "";
export const selectSortBy = (state: any): "country" | "region" => selectSlice(state).sortBy ?? "country";
export const selectSortOrder = (state: any): "asc" | "desc" => selectSlice(state).sortOrder ?? "asc";

export const selectViewRegionsModalOpen = (state: any): boolean => !!selectSlice(state).viewRegionsModalOpen;
export const selectSaving = (state: any): boolean => !!selectSlice(state).saving;
export const selectSaveError = (state: any): string | undefined => selectSlice(state).saveError;
export const selectSaveSuccessUnassigned = (
  state: any
): Array<{ countryId: number; countryCode: string; countryName: string }> =>
  selectSlice(state).saveSuccessUnassigned ?? [];

function normalizeId(v: string | number): string {
  return String(v);
}

export const selectEffectiveCountryRegion = (
  state: any,
  countryId: string | number,
  baseRegionId: number | null
): number | null => {
  const edits = selectCountryEdits(state);
  const key = normalizeId(countryId);
  if (key in edits) return edits[key];
  return baseRegionId;
};

export const selectFilteredAndSortedCountries = (state: any): CountryWithRegionDto[] => {
  const slice = selectSlice(state);
  const countries = slice.countries ?? [];
  const searchTerm = (slice.searchTerm ?? "").trim().toLowerCase();
  const edits = slice.countryEdits ?? {};
  const sortBy = slice.sortBy ?? "country";
  const sortOrder = slice.sortOrder ?? "asc";

  let filtered = searchTerm
    ? countries.filter((c) => (c.name ?? "").toLowerCase().includes(searchTerm))
    : [...countries];

  const effectiveRegionId = (c: CountryWithRegionDto) => {
    const key = String(c.id);
    return key in edits ? edits[key] : c.regionId;
  };

  if (sortBy === "country") {
    filtered.sort((a, b) => {
      const cmp = (a.name ?? "").localeCompare(b.name ?? "");
      return sortOrder === "asc" ? cmp : -cmp;
    });
  } else {
    filtered.sort((a, b) => {
      const aAssigned = effectiveRegionId(a) != null ? 1 : 0;
      const bAssigned = effectiveRegionId(b) != null ? 1 : 0;
      if (aAssigned !== bAssigned) return aAssigned - bAssigned;
      const aName = (a.name ?? "").toLowerCase();
      const bName = (b.name ?? "").toLowerCase();
      const cmp = aName.localeCompare(bName);
      return sortOrder === "asc" ? cmp : -cmp;
    });
  }
  return filtered;
};

export const selectDirtyCountryIds = (state: any): string[] => {
  const edits = selectCountryEdits(state);
  return Object.keys(edits).filter(() => true);
};

export const selectHasUnsavedChanges = (state: any): boolean => {
  const edits = selectCountryEdits(state);
  return Object.keys(edits).length > 0;
};

export const selectAllCountriesHaveRegion = (state: any): boolean => {
  const slice = selectSlice(state);
  const countries = slice.countries ?? [];
  const edits = slice.countryEdits ?? {};
  for (const c of countries) {
    const key = normalizeId(c.id);
    const regionId = key in edits ? edits[key] : c.regionId;
    if (regionId == null) return false;
  }
  return true;
};